"use client";

import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import { IconPlus } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { Img } from "@/components/image";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AddonSellerName } from "@/features/addons/components/addon-seller-name";
import { useAddonsConfigStore } from "@/features/addons/components/addons-config-provider";
import { useAddonSelectItemEvent } from "@/features/analytics/hooks/use-addon-select-item-event";
import type { AddonListType } from "@/features/analytics/hooks/use-addon-view-item-list-event";
import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";

type DietAddonCardProps = {
  addon: AddonDietAddonsRead;
  onAddAddonAction?: (addon: AddonDietAddonsRead) => void;
  listType?: AddonListType;
  categoryName?: string;
  cateringName?: string;
  index?: number;
};

export const DietAddonCard = ({
  addon,
  onAddAddonAction,
  listType,
  categoryName,
  cateringName,
  index,
}: DietAddonCardProps) => {
  const t = useTranslations();
  const reportSelectedAddon = useAddonSelectItemEvent();

  const handleSelect = () => {
    if (listType && index !== undefined) {
      reportSelectedAddon({
        addon,
        cateringName,
        categoryName,
        index,
        listType,
      });
    }
    onAddAddonAction?.(addon);
  };

  // Total units of this addon currently selected — the sum of the per-day
  // quantities, not the number of days (one store entry per day, each with its
  // own quantity). When > 0 the "+" button becomes that count and re-opens the
  // modal for editing.
  const selectedQuantity = useAddonsConfigStore((store) =>
    store.addons.reduce(
      (total, entry) =>
        entry.addonId === addon.id ? total + entry.quantity : total,
      0,
    ),
  );

  const { promoPrice, price } = addon;
  const hasPromo = promoPrice != null;
  const discountPercent =
    promoPrice != null && price > 0
      ? Math.round(100 - (promoPrice * 100) / price)
      : 0;

  return (
    <Card className="relative flex h-full w-full flex-col overflow-hidden rounded-xl border border-brand-grey2-300 bg-brand-white shadow-none">
      {/* `fill` takes the image out of flow, so nothing contributes to this
          box's content height and `aspect-square` alone drives it. With an
          in-flow image a portrait asset stretched the box past the square. */}
      <div className="relative aspect-square w-full overflow-hidden">
        <Img
          alt={addon.name}
          className="object-cover"
          fill
          sizes="(min-width: 768px) 224px, 50vw"
          src={addon.imageUrl ?? "/placeholder.svg"}
        />
        {hasPromo && discountPercent > 0 && (
          <span className="absolute top-2 left-2 flex h-6.5 items-center rounded-full bg-brand-red-50 px-2 py-1 font-medium text-brand-red-300 text-xs leading-4.5">
            -{discountPercent}%
          </span>
        )}
        <Button
          aria-label={
            selectedQuantity > 0
              ? t("cart.configuration.addons.summary.menu.edit")
              : t("cart.configuration.addons.add", { name: addon.name })
          }
          className={cn(
            "absolute right-2 bottom-2 z-10 h-auto min-h-0 w-auto rounded-xl border border-brand-green2-300 p-3",
            "bg-brand-white text-brand-green2-300 hover:bg-brand-green2-300 hover:text-brand-white",
          )}
          onClick={handleSelect}
          type="button"
          variant="ghost"
        >
          {selectedQuantity > 0 ? (
            <span className="flex size-4 items-center justify-center text-base leading-none">
              {selectedQuantity}
            </span>
          ) : (
            <IconPlus className="size-4" stroke={2} />
          )}
        </Button>
      </div>

      <CardContent className="flex flex-1 flex-col gap-2 p-3">
        <div className="flex flex-col">
          <AddonSellerName fallback={cateringName} name={addon.merchantName} />
          <h3 className="line-clamp-2 font-medium text-brand-green1-300 text-sm leading-[22px]">
            {addon.name}
          </h3>
        </div>

        <div className="mt-auto space-y-1">
          <div className="flex flex-wrap items-center gap-1">
            {/* The red promotional price comes first, the regular price follows
                struck through — only when the addon actually has a promo. */}
            {hasPromo && (
              <span className="font-bold text-base text-brand-red-300 leading-6">
                {getLocalePrice({ price: promoPrice })}
              </span>
            )}
            <span
              className={cn("font-bold text-base text-brand-green1-300", {
                "font-normal text-brand-black-100 text-sm leading-[22px] line-through":
                  hasPromo,
              })}
            >
              {getLocalePrice({ price })}
            </span>
          </div>
          {/* TODO: Omnibus */}
          {/* {daysCount ? (
            <span className="text-brand-grey5 text-xs leading-4">
              {t("cart.configuration.addons.priceFromDays", {
                days: daysCount,
                price: getLocalePrice({ price }),
              })}
            </span>
          ) : null} */}
        </div>
      </CardContent>
    </Card>
  );
};
